import { useTranslation } from 'react-i18next';
import { LegalShell, LegalTitle, LegalH2, LegalDivider } from './shell';

/** Public /about — a detailed, bilingual (EN + BG) explanation of how the app
 *  uses Google Calendar. Written for Google's OAuth verification (which wants a
 *  clear description of scope usage) and for patients/clinics. Reuses the legal
 *  shell; the back link points home. */
export default function AboutPage() {
  const { t } = useTranslation();
  return (
    <LegalShell backTo="/" backLabel={t('legal.backToHome')}>
      {/* ---------------------------------------------------------------- EN */}
      <LegalTitle>How Balance clinic ai agent uses Google Calendar</LegalTitle>
      <p>
        Balance clinic ai agent is appointment-scheduling software with an AI voice assistant for
        clinics. It offers an <b>optional</b> Google Calendar integration for individual specialists
        (doctors). This page explains exactly what we access, why, and how — nothing else about the
        Google account is touched.
      </p>

      <LegalH2>Connecting a calendar</LegalH2>
      <p>
        A specialist connects <b>their own</b> Google account through Google’s standard OAuth consent
        screen, started from their profile inside the app. Connecting is voluntary and can be undone
        at any time. If a specialist does not connect Google, the app simply uses its own internal
        schedule instead — no Google data is involved for that doctor.
      </p>

      <LegalH2>Permissions (scopes) we request</LegalH2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <b>See events on the calendar</b> (<code>calendar.readonly</code>) — to read the doctor’s
          free/busy times and existing events so we know when they are available.
        </li>
        <li>
          <b>Manage events on the calendar</b> (<code>calendar.events</code>) — to create, move and
          remove the appointment events we book on the doctor’s behalf.
        </li>
      </ul>

      <LegalH2>What we read</LegalH2>
      <p>
        We read the calendar’s <b>free/busy</b> information and event details (title, description,
        location, start/end times) for the connected doctor, only for the date range being scheduled.
        This lets the app compute which appointment slots are actually available and avoid
        double-booking a time when the doctor is already busy.
      </p>

      <LegalH2>What we write</LegalH2>
      <p>
        When an appointment is booked — by clinic staff in the app or by the AI voice assistant on a
        phone call — we create a corresponding event on the doctor’s Google Calendar (with the
        patient’s name, time and reason). If the appointment is rescheduled we update that same event;
        if it is cancelled we remove it. We never modify unrelated events.
      </p>

      <LegalH2>How the AI assistant uses it</LegalH2>
      <p>
        When a patient calls, the assistant reads the doctor’s availability (computed from the
        calendar above) to offer real free slots, then books, reschedules or cancels the appointment —
        keeping the doctor’s calendar and the clinic’s records in sync.
      </p>
      <p>
        The assistant is provided by <b>ElevenLabs</b> (Conversational AI, on the pay-as-you-go plan),
        running on <b>Google Gemini</b> for English calls and <b>Qwen 3.5</b> for Bulgarian calls.
        These are the only downstream models; we use no other AI provider, aggregator or gateway. The
        assistant has <b>no access to Google Calendar data</b>: our app acts only as an intermediary
        (a “router”) and passes the assistant nothing but the computed free and booked slot times as
        plain text. Calendar event content — titles, descriptions, locations and attendees — is never
        sent to ElevenLabs, Gemini, Qwen or any other AI provider, and specialists, patients and slots
        are referenced only by our own internal identifiers, never by linking a name to a specific
        calendar. No raw, aggregated or derived Google user data is used to create, train or improve
        any AI/ML model.
      </p>

      <LegalH2>Storage, security and Limited Use</LegalH2>
      <p>
        The Google authorization (refresh token) is encrypted at rest (AES-256-GCM) and access is
        limited to authorised personnel. Balance clinic ai agent’s use and transfer of information
        received from Google APIs adheres to the{' '}
        <a
          className="text-primary hover:underline"
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements: the data is used only to provide the scheduling
        features described here, is not used for advertising, is not sold, and is not transferred to
        others except as needed to provide the service or as required by law.
      </p>

      <LegalH2>Your control</LegalH2>
      <p>
        A specialist can disconnect Google Calendar at any time from within the app, and can revoke
        the app’s access at{' '}
        <a
          className="text-primary hover:underline"
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noreferrer"
        >
          myaccount.google.com/permissions
        </a>
        . Questions:{' '}
        <a className="text-primary hover:underline" href="mailto:info@diadraw.com">
          info@diadraw.com
        </a>
        .
      </p>

      {/* ---------------------------------------------------------------- BG */}
      <LegalDivider label="Български" />
      <LegalTitle>Как Balance clinic ai agent ползва Google Calendar</LegalTitle>
      <p>
        Balance clinic ai agent е софтуер за записване на часове с AI гласов асистент за клиники. Той
        предлага <b>по избор</b> интеграция с Google Calendar за отделните специалисти (лекари). Тази
        страница обяснява точно какво достъпваме, защо и как — нищо друго от Google профила не се пипа.
      </p>

      <LegalH2>Свързване на календар</LegalH2>
      <p>
        Специалистът свързва <b>своя собствен</b> Google профил през стандартния OAuth екран на Google,
        стартиран от профила му в приложението. Свързването е доброволно и може да се прекрати по всяко
        време. Ако лекар не свърже Google, приложението просто ползва вътрешния си график — за този
        лекар не се използват Google данни.
      </p>

      <LegalH2>Права (scopes), които искаме</LegalH2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <b>Четене на събитията в календара</b> (<code>calendar.readonly</code>) — за да четем
          свободно/заето и съществуващите събития и да знаем кога лекарят е свободен.
        </li>
        <li>
          <b>Управление на събитията в календара</b> (<code>calendar.events</code>) — за да създаваме,
          местим и премахваме събитията за часовете, които записваме от името на лекаря.
        </li>
      </ul>

      <LegalH2>Какво четем</LegalH2>
      <p>
        Четем информацията за <b>свободно/заето</b> и детайлите на събитията (заглавие, описание,
        локация, начало/край) за свързания лекар, само за периода, който се планира. Така приложението
        изчислява кои часове реално са свободни и не записва час, когато лекарят вече е зает.
      </p>

      <LegalH2>Какво записваме</LegalH2>
      <p>
        Когато се запише час — от персонала в приложението или от AI асистента по телефона — създаваме
        съответно събитие в Google календара на лекаря (с име на пациента, час и причина). При
        преместване обновяваме същото събитие; при отказ го премахваме. Никога не променяме чужди
        събития.
      </p>

      <LegalH2>Как го ползва AI асистентът</LegalH2>
      <p>
        Когато пациент се обади, асистентът чете свободните часове на лекаря (изчислени от календара
        по-горе), предлага реални свободни слотове и записва, мести или отменя часа — така календарът на
        лекаря и записите на клиниката остават синхронизирани.
      </p>
      <p>
        Асистентът се предоставя от <b>ElevenLabs</b> (Conversational AI, на плана pay-as-you-go) и
        работи с <b>Google Gemini</b> за разговори на английски и <b>Qwen 3.5</b> за разговори на
        български. Това са единствените downstream модели; не ползваме друг доставчик на AI, агрегатор
        или gateway. Асистентът <b>няма достъп до данни от Google Calendar</b>: приложението действа
        само като посредник („рутер“) и подава на асистента единствено изчислените свободни и заети
        часове като обикновен текст. Съдържанието на събитията — заглавия, описания, локации и
        участници — никога не се изпраща към ElevenLabs, Gemini, Qwen или друг доставчик на AI, а
        специалистите, пациентите и часовете се обозначават само с наши вътрешни идентификатори, без да
        свързваме име с конкретен календар. Никакви сурови, обобщени или производни Google данни не се
        използват за създаване, обучение или подобряване на AI/ML модел.
      </p>

      <LegalH2>Съхранение, сигурност и Ограничено използване</LegalH2>
      <p>
        Google оторизацията (refresh token) се пази криптирана (AES-256-GCM), а достъпът е ограничен до
        оторизиран персонал. Използването и прехвърлянето от Balance clinic ai agent на информация,
        получена от Google API, спазва{' '}
        <a
          className="text-primary hover:underline"
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , включително изискванията за Ограничено използване: данните се ползват само за описаните тук
        функции по записване, не се ползват за реклама, не се продават и не се прехвърлят на трети
        страни освен когато е необходимо за услугата или го изисква законът.
      </p>

      <LegalH2>Вашият контрол</LegalH2>
      <p>
        Специалистът може по всяко време да прекъсне Google Calendar от приложението и да отнеме достъпа
        на{' '}
        <a
          className="text-primary hover:underline"
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noreferrer"
        >
          myaccount.google.com/permissions
        </a>
        . Въпроси:{' '}
        <a className="text-primary hover:underline" href="mailto:info@diadraw.com">
          info@diadraw.com
        </a>
        .
      </p>
    </LegalShell>
  );
}
